import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardActions,
  Typography,
  Avatar,
  Chip,
  IconButton,
  Tooltip,
  Grid,
  Collapse,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  Button,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  TextField
} from '@mui/material';
import {
  Face as FaceIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Search as SearchIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  PhotoLibrary as PhotoLibraryIcon,
  Visibility as VisibilityIcon
} from '@mui/icons-material';
import faceRecognitionService from '../services/faceRecognitionService';
import { useAuthenticatedImage } from '../utils/imageUtils';
import FaceRecognitionResultsDialog from './FaceRecognitionResultsDialog';

// Enhanced Authenticated Avatar with error handling
const EnhancedAuthenticatedAvatar = ({ imageId, alt, size = 60, showStar = false, ...props }) => {
  const { imageUrl, loading, error } = useAuthenticatedImage(imageId, 'thumbnail');
  const { imageUrl: fallbackUrl } = useAuthenticatedImage(error && imageId ? imageId : null, 'serve');
  
  if (loading && imageId) {
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          width: size, 
          height: size,
          position: 'relative'
        }}
      >
        <CircularProgress size={size * 0.3} />
        {showStar && (
          <StarIcon 
            sx={{ 
              position: 'absolute', 
              top: -4, 
              right: -4, 
              color: 'warning.main',
              fontSize: 16
            }} 
          />
        )}
      </Box>
    );
  }
  
  return (
    <Box sx={{ position: 'relative', display: 'inline-block' }}>
      <Avatar
        src={imageUrl || fallbackUrl}
        alt={alt}
        sx={{ 
          width: size, 
          height: size,
          border: showStar ? '2px solid' : 'none',
          borderColor: showStar ? 'warning.main' : 'transparent'
        }}
        {...props}
      >
        {(!imageUrl && !fallbackUrl) && <FaceIcon />}
      </Avatar>
      {showStar && (
        <StarIcon 
          sx={{ 
            position: 'absolute', 
            top: -4, 
            right: -4, 
            color: 'warning.main',
            backgroundColor: 'background.paper',
            borderRadius: '50%',
            fontSize: 16
          }} 
        />
      )}
    </Box>
  );
};

// Photo Management Dialog
const PhotoManagementDialog = ({ 
  open, 
  onClose, 
  person, 
  sessionId, 
  onPhotoRemoved, 
  onPrimaryChanged 
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleRemovePhoto = async (imageId) => {
    setLoading(true);
    setError(null);
    try {
      await faceRecognitionService.removeReferencePhoto(sessionId, imageId);
      onPhotoRemoved(imageId);
      if (person.referencePhotoDetails.length <= 1) {
        onClose(); // Close dialog if no photos left
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to remove photo');
    } finally {
      setLoading(false);
    }
  };

  const handleSetPrimary = async (imageId) => {
    setLoading(true);
    setError(null);
    try {
      await faceRecognitionService.setPrimaryReferencePhoto(sessionId, imageId);
      onPrimaryChanged(imageId);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to set primary photo');
    } finally {
      setLoading(false);
    }
  };

  if (!person) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FaceIcon />
          <Typography variant="h6">
            Manage Photos for {person.personName}
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Total Reference Photos: {person.stats.totalReferencePhotos}
        </Typography>

        <Grid container spacing={2}>
          {person.referencePhotoDetails?.map((photo) => (
            <Grid item xs={12} sm={6} md={4} key={photo.imageId}>
              <Card 
                variant="outlined" 
                sx={{ 
                  position: 'relative',
                  border: photo.isPrimary ? '2px solid' : '1px solid',
                  borderColor: photo.isPrimary ? 'warning.main' : 'divider'
                }}
              >
                <Box sx={{ position: 'relative', p: 2, textAlign: 'center' }}>
                  <EnhancedAuthenticatedAvatar
                    imageId={photo.imageId}
                    alt={`${person.personName} - ${photo.imageName}`}
                    size={120}
                    showStar={photo.isPrimary}
                  />
                  
                  {photo.isPrimary && (
                    <Chip
                      label="Primary"
                      size="small"
                      color="warning"
                      sx={{ 
                        position: 'absolute', 
                        top: 8, 
                        right: 8,
                        fontSize: '0.7rem'
                      }}
                    />
                  )}
                </Box>
                
                <CardContent sx={{ pt: 0 }}>
                  <Typography variant="body2" noWrap>
                    {photo.imageName || 'Unnamed'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Confidence: {Math.round(photo.confidence)}%
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Added: {new Date(photo.addedAt).toLocaleDateString()}
                  </Typography>
                </CardContent>
                
                <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
                  <Tooltip title={photo.isPrimary ? "Already primary" : "Set as primary photo"}>
                    <span>
                      <IconButton
                        size="small"
                        onClick={() => handleSetPrimary(photo.imageId)}
                        disabled={photo.isPrimary || loading}
                        color={photo.isPrimary ? "warning" : "default"}
                      >
                        {photo.isPrimary ? <StarIcon /> : <StarBorderIcon />}
                      </IconButton>
                    </span>
                  </Tooltip>
                  
                  <Tooltip title="Remove this reference photo">
                    <span>
                      <IconButton
                        size="small"
                        onClick={() => handleRemovePhoto(photo.imageId)}
                        disabled={loading}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <CircularProgress size={24} />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

// Main Enhanced Reference Persons List
// When `persons` prop is provided the component skips its own fetch and uses parent-owned data.
const EnhancedReferencePersonsList = ({ sessionId, onDelete, onFind, refreshTrigger, persons: personsProp }) => {
  const [referencePersons, setReferencePersons] = useState(personsProp || []);
  const [loading, setLoading] = useState(false);

  // Sync from parent whenever the prop array changes (covers initial load + refresh)
  useEffect(() => {
    if (personsProp !== undefined) {
      setReferencePersons(personsProp);
    }
  }, [personsProp]);
  const [error, setError] = useState(null);
  const [expandedPersons, setExpandedPersons] = useState(new Set());
  const [photoManagementDialog, setPhotoManagementDialog] = useState({
    open: false,
    person: null
  });
  const [renameDialog, setRenameDialog] = useState({
    open: false,
    person: null,
    newName: '',
    saving: false,
    error: null
  });
  const [resultsDialog, setResultsDialog] = useState({
    open: false,
    referencePersonId: null,
    referencePersonName: '',
    referenceImage: null
  });

  const loadReferencePersons = useCallback(async () => {
    if (personsProp !== undefined) return; // data owned by parent — skip self-fetch
    setLoading(true);
    setError(null);
    
    try {
      console.log(`🔍 Loading enhanced reference persons for session: ${sessionId}`);
      const response = await faceRecognitionService.getSessionReferencePersons(sessionId);
      
      if (response && response.referencePersons && Array.isArray(response.referencePersons)) {
        setReferencePersons(response.referencePersons);
        console.log('✅ Enhanced reference persons loaded:', response.referencePersons);
      } else {
        console.warn('⚠️ Unexpected response format:', response);
        setReferencePersons([]);
      }
    } catch (err) {
      console.error('❌ Error loading enhanced reference persons:', err);
      setError(err.response?.data?.error || 'Failed to load reference persons');
    } finally {
      setLoading(false);
    }
  }, [sessionId, personsProp]);

  useEffect(() => {
    if (personsProp === undefined && sessionId) {
      loadReferencePersons();
    }
  }, [sessionId, refreshTrigger, loadReferencePersons, personsProp]);

  const handleToggleExpanded = (personId) => {
    setExpandedPersons(prev => {
      const newSet = new Set(prev);
      if (newSet.has(personId)) {
        newSet.delete(personId);
      } else {
        newSet.add(personId);
      }
      return newSet;
    });
  };

  const handleDeletePerson = async (person) => {
    try {
      await faceRecognitionService.deleteReferencePerson(sessionId, person.uniqueId);
      setReferencePersons(prev => 
        prev.filter(p => p.uniqueId !== person.uniqueId)
      );
      if (onDelete) onDelete(person.uniqueId);
    } catch (err) {
      console.error('Error deleting reference person:', err);
      setError(err.response?.data?.error || 'Failed to delete reference person');
    }
  };

  const handleFindMatches = (person) => {
    console.log("Finding matches for person:", person);
    setResultsDialog({
      open: true,
      referencePersonId: person.uniqueId,
      referencePersonName: person.personName,
      referenceImage: person.primaryImage || {
        imageId: person.referencePhotoDetails?.[0]?.imageId,
        name: person.personName
      }
    });
  };

  const handleManagePhotos = (person) => {
    setPhotoManagementDialog({
      open: true,
      person
    });
  };

  const handlePhotoRemoved = (imageId) => {
    // Refresh the reference persons list
    loadReferencePersons();
  };

  const handlePrimaryChanged = (imageId) => {
    // Refresh the reference persons list
    loadReferencePersons();
  };

  const handleRenameOpen = (person) => {
    setRenameDialog({ open: true, person, newName: person.personName, saving: false, error: null });
  };

  const handleRenameClose = () => {
    setRenameDialog({ open: false, person: null, newName: '', saving: false, error: null });
  };

  const handleRenameSave = async () => {
    const { person, newName } = renameDialog;
    if (!newName.trim()) return;
    setRenameDialog((prev) => ({ ...prev, saving: true, error: null }));
    try {
      await faceRecognitionService.renameReferencePerson(sessionId, person.uniqueId, newName);
      setReferencePersons((prev) =>
        prev.map((p) => p.uniqueId === person.uniqueId ? { ...p, personName: newName.trim() } : p)
      );
      handleRenameClose();
      if (onDelete) onDelete(); // trigger parent refresh
    } catch (err) {
      setRenameDialog((prev) => ({
        ...prev,
        saving: false,
        error: err.response?.data?.error || err.message || 'Failed to rename person'
      }));
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
        <CircularProgress size={24} />
        <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
          Loading reference persons...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ my: 2 }}>
        {error}
      </Alert>
    );
  }

  if (referencePersons.length === 0) {
    return (
      <Card sx={{ my: 2, textAlign: 'center', p: 3 }}>
        <FaceIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
        <Typography variant="h6" gutterBottom>
          No Reference Persons Set
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Tag a person in one of your photos to find them across all images in this session.
        </Typography>
      </Card>
    );
  }

  return (
    <Box sx={{ my: 2 }}>
      <Typography variant="h6" gutterBottom>
        <FaceIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
        Reference Persons ({referencePersons.length})
      </Typography>
      
      <Grid container spacing={2}>
        {referencePersons.map((person) => {
          const isExpanded = expandedPersons.has(person.uniqueId);
          const primaryPhoto = person.referencePhotoDetails?.find(p => p.isPrimary) || 
                              person.referencePhotoDetails?.[0];
          
          return (
            <Grid item xs={12} key={person.uniqueId}>
              <Card variant="outlined">
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <EnhancedAuthenticatedAvatar
                      imageId={primaryPhoto?.imageId}
                      alt={person.personName}
                      size={80}
                      showStar={true}
                    />
                    
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="h6" gutterBottom>
                        {person.personName}
                      </Typography>
                      
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                        <Chip 
                          size="small" 
                          label={`${person.stats.totalReferencePhotos} photo${person.stats.totalReferencePhotos !== 1 ? 's' : ''}`}
                          color="primary"
                          icon={<PhotoLibraryIcon />}
                        />
                        <Chip 
                          size="small" 
                          label={`${person.stats.imagesWithMatches || 0} image${(person.stats.imagesWithMatches || 0) !== 1 ? 's' : ''} with matches`}
                          color="secondary"
                          icon={<VisibilityIcon />}
                        />
                      </Box>
                      
                      <Typography variant="body2" color="text.secondary">
                        Created: {new Date(person.createdAt).toLocaleDateString()}
                        {person.stats.lastMatchedAt && (
                          <> • Last found: {new Date(person.stats.lastMatchedAt).toLocaleDateString()}</>
                        )}
                      </Typography>
                    </Box>
                    
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Tooltip title="Find this person in all photos">
                        <IconButton 
                          color="primary"
                          onClick={() => handleFindMatches(person)}
                        >
                          <SearchIcon />
                        </IconButton>
                      </Tooltip>
                      
                      <Tooltip title="Manage photos">
                        <IconButton 
                          color="secondary"
                          onClick={() => handleManagePhotos(person)}
                        >
                          <PhotoLibraryIcon />
                        </IconButton>
                      </Tooltip>
                      
                      <Tooltip title="Rename person">
                        <IconButton
                          color="default"
                          onClick={() => handleRenameOpen(person)}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="Delete reference person">
                        <IconButton 
                          color="error"
                          onClick={() => handleDeletePerson(person)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                      
                      {person.stats.totalReferencePhotos > 1 && (
                        <Tooltip title={isExpanded ? "Show less" : "Show all photos"}>
                          <IconButton onClick={() => handleToggleExpanded(person.uniqueId)}>
                            {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </Box>
                  
                  <Collapse in={isExpanded && person.stats.totalReferencePhotos > 1}>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle2" gutterBottom>
                      All Reference Photos:
                    </Typography>
                    <List dense>
                      {person.referencePhotoDetails?.map((photo) => (
                        <ListItem key={photo.imageId}>
                          <ListItemAvatar>
                            <EnhancedAuthenticatedAvatar
                              imageId={photo.imageId}
                              alt={photo.imageName}
                              size={40}
                              showStar={photo.isPrimary}
                            />
                          </ListItemAvatar>
                          <ListItemText
                            primary={photo.imageName || 'Unnamed'}
                            secondary={
                              <>
                                Confidence: {Math.round(photo.confidence)}%
                                <br />
                                {photo.isPrimary ? 'Primary photo' : `Added: ${new Date(photo.addedAt).toLocaleDateString()}`}
                              </>
                            }
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Collapse>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* Rename Dialog */}
      <Dialog open={renameDialog.open} onClose={handleRenameClose} maxWidth="xs" fullWidth>
        <DialogTitle>Rename Person</DialogTitle>
        <DialogContent>
          {renameDialog.error && (
            <Alert severity="error" sx={{ mb: 2 }}>{renameDialog.error}</Alert>
          )}
          <TextField
            autoFocus
            fullWidth
            label="New name"
            value={renameDialog.newName}
            onChange={(e) => setRenameDialog((prev) => ({ ...prev, newName: e.target.value }))}
            onKeyDown={(e) => e.key === 'Enter' && !renameDialog.saving && handleRenameSave()}
            disabled={renameDialog.saving}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleRenameClose} disabled={renameDialog.saving}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleRenameSave}
            disabled={renameDialog.saving || !renameDialog.newName.trim()}
          >
            {renameDialog.saving ? <CircularProgress size={20} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Photo Management Dialog */}
      <PhotoManagementDialog
        open={photoManagementDialog.open}
        onClose={() => setPhotoManagementDialog({ open: false, person: null })}
        person={photoManagementDialog.person}
        sessionId={sessionId}
        onPhotoRemoved={handlePhotoRemoved}
        onPrimaryChanged={handlePrimaryChanged}
      />

      {/* Results Dialog */}
      {resultsDialog.open && (
        <FaceRecognitionResultsDialog
          open={resultsDialog.open}
          onClose={() => setResultsDialog({
            open: false,
            referencePersonId: null,
            referencePersonName: '',
            referenceImage: null
          })}
          sessionId={sessionId}
          referencePersonId={resultsDialog.referencePersonId}
          referencePersonName={resultsDialog.referencePersonName}
          referenceImage={resultsDialog.referenceImage}
        />
      )}
    </Box>
  );
};

export default EnhancedReferencePersonsList;
